import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    Chip,
    Skeleton,
    Grid2 as Grid,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import { Delivery, DeliveryReportPage as ReportPage } from 'typings/models';
import { getDeliveryReport, downloadDeliveryReport, startZipExport, getZipStatus, ReportFilters } from 'api/deliveries';
import { getDrivers } from 'api/drivers';
import { Driver } from 'typings/models';
import { formatDateTime } from 'misc/dateUtils';

const toDateStr = (d: Date) => d.toISOString().split('T')[0];

const DeliveryReportPage = () => {
    const today = new Date();
    const fifteenDaysAgo = new Date(today);
    fifteenDaysAgo.setDate(today.getDate() - 15);

    const [dateFrom, setDateFrom] = useState(toDateStr(fifteenDaysAgo));
    const [dateTo, setDateTo] = useState(toDateStr(today));
    const [reviewedFilter, setReviewedFilter] = useState<'all' | 'yes' | 'no'>('all');
    const [statusFilter, setStatusFilter] = useState<'all' | 'completed' | 'failed'>('completed');
    const [driverId, setDriverId] = useState('');
    const [clientSearch, setClientSearch] = useState('');
    const [drivers, setDrivers] = useState<Driver[]>([]);

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [searchKey, setSearchKey] = useState(0);
    const [data, setData] = useState<ReportPage | null>(null);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [zipJobId, setZipJobId] = useState<string | null>(null);
    const [zipStatus, setZipStatus] = useState<'idle' | 'generating' | 'ready' | 'error'>('idle');
    const [zipUrl, setZipUrl] = useState<string | null>(null);
    const zipPollRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        getDrivers().then(setDrivers);
        return () => { if (zipPollRef.current) clearInterval(zipPollRef.current); };
    }, []);

    const buildFilters = (): ReportFilters => ({
        reviewed: reviewedFilter === 'all' ? null : reviewedFilter === 'yes',
        driverId: driverId || undefined,
        clientSearch: clientSearch || undefined,
        statusFilter: statusFilter === 'all' ? undefined : statusFilter,
    });

    useEffect(() => {
        setLoading(true);
        getDeliveryReport(dateFrom, dateTo, page + 1, pageSize, buildFilters()).then((result) => {
            setData(result);
            setLoading(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize, searchKey]);

    const handleSearch = () => {
        setPage(0);
        setSearchKey((k) => k + 1);
    };

    const handleDownload = async () => {
        setDownloading(true);
        await downloadDeliveryReport(dateFrom, dateTo, buildFilters());
        setDownloading(false);
    };

    const handleGenerateZip = async () => {
        setZipStatus('generating');
        setZipUrl(null);
        const jobId = await startZipExport(dateFrom, dateTo, buildFilters());
        if (!jobId) { setZipStatus('error'); return; }
        setZipJobId(jobId);
        zipPollRef.current = setInterval(async () => {
            const result = await getZipStatus(jobId);
            if (!result) return;
            if (result.status === 'ready') {
                clearInterval(zipPollRef.current!);
                setZipUrl(result.url!);
                setZipStatus('ready');
            } else if (result.status === 'error') {
                clearInterval(zipPollRef.current!);
                setZipStatus('error');
            }
        }, 3000);
    };

    const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
    const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPageSize(parseInt(e.target.value, 10));
        setPage(0);
    };


    return (
        <Box p={2}>
            <Typography variant="h6" gutterBottom>
                Reporte de Entregas
            </Typography>

            <Grid container spacing={2} alignItems="flex-end" sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <TextField
                        label="Desde"
                        type="date"
                        fullWidth
                        size="small"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <TextField
                        label="Hasta"
                        type="date"
                        fullWidth
                        size="small"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Revisado</InputLabel>
                        <Select
                            label="Revisado"
                            value={reviewedFilter}
                            onChange={(e) => setReviewedFilter(e.target.value as any)}
                        >
                            <MenuItem value="all">Todos</MenuItem>
                            <MenuItem value="yes">Sí</MenuItem>
                            <MenuItem value="no">No</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Estado</InputLabel>
                        <Select
                            label="Estado"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value as any)}
                        >
                            <MenuItem value="all">Todos</MenuItem>
                            <MenuItem value="completed">Entregado</MenuItem>
                            <MenuItem value="failed">Problema</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Conductor</InputLabel>
                        <Select
                            label="Conductor"
                            value={driverId}
                            onChange={(e) => setDriverId(e.target.value)}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {drivers.map((d) => (
                                <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <TextField
                        label="Cliente"
                        fullWidth
                        size="small"
                        value={clientSearch}
                        onChange={(e) => setClientSearch(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        placeholder="Buscar por nombre..."
                    />
                </Grid>
                <Grid size={{ xs: 6, sm: 3, md: 1 }}>
                    <Button variant="contained" fullWidth onClick={handleSearch} disabled={loading}>
                        Buscar
                    </Button>
                </Grid>
                <Grid size={{ xs: 6, sm: 3, md: 1 }}>
                    <Button
                        variant="outlined"
                        fullWidth
                        startIcon={<DownloadIcon />}
                        onClick={handleDownload}
                        disabled={downloading || !data || data.total === 0}
                    >
                        {downloading ? '...' : 'Excel'}
                    </Button>
                </Grid>
                <Grid size={{ xs: 6, sm: 3, md: 1 }}>
                    <Button
                        variant="outlined"
                        fullWidth
                        color="secondary"
                        startIcon={<FolderZipIcon />}
                        onClick={handleGenerateZip}
                        disabled={zipStatus === 'generating' || !data || data.total === 0}
                    >
                        {zipStatus === 'generating' ? '...' : 'ZIP'}
                    </Button>
                </Grid>
            </Grid>

            {zipStatus === 'generating' && (
                <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">Generando ZIP, esto puede tomar unos minutos...</Typography>
                </Box>
            )}
            {zipStatus === 'ready' && zipUrl && (
                <Box sx={{ mb: 1 }}>
                    <Button variant="contained" color="success" startIcon={<DownloadIcon />} href={zipUrl} target="_blank" rel="noopener noreferrer">
                        Descargar ZIP
                    </Button>
                </Box>
            )}
            {zipStatus === 'error' && (
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>Error generando el ZIP. Intenta de nuevo.</Typography>
            )}

            {data && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {data.total} resultado{data.total !== 1 ? 's' : ''}
                </Typography>
            )}

            <TableContainer component={Paper}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell><strong>Conductor</strong></TableCell>
                            <TableCell><strong>Factura</strong></TableCell>
                            <TableCell><strong>Estado</strong></TableCell>
                            <TableCell><strong>Cliente</strong></TableCell>
                            <TableCell><strong>Fecha Entrega</strong></TableCell>
                            <TableCell align="center"><strong>Archivos</strong></TableCell>
                            <TableCell align="center"><strong>Revisado</strong></TableCell>
                            <TableCell><strong>Fecha Revisión</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            Array.from({ length: 5 }).map((_, i) => (
                                <TableRow key={i}>
                                    {Array.from({ length: 8 }).map((__, j) => (
                                        <TableCell key={j}><Skeleton /></TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : data?.items.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    <Typography color="text.secondary" py={3}>
                                        No se encontraron entregas con los filtros seleccionados.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            data?.items.map((d: Delivery) => (
                                <TableRow key={d.id} hover>
                                    <TableCell>{d.driver?.name ?? '—'}</TableCell>
                                    <TableCell>{d.order?.id ?? '—'}</TableCell>
                                    <TableCell>
                                        {d.status === 'Failed'
                                            ? <Chip label="Problema" color="error" size="small" />
                                            : <Chip label="Entregado" color="success" size="small" />}
                                    </TableCell>
                                    <TableCell>{d.order?.client?.name ?? '—'}</TableCell>
                                    <TableCell>{formatDateTime(d.completed_at)}</TableCell>
                                    <TableCell align="center">{d.delivery_files?.length ?? 0}</TableCell>
                                    <TableCell align="center">
                                        {d.reviewed
                                            ? <Chip label="Sí" color="success" size="small" />
                                            : <Chip label="No" size="small" />}
                                    </TableCell>
                                    <TableCell>{formatDateTime(d.reviewed_at)}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={data?.total ?? 0}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={pageSize}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 20, 50, 100]}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            />
        </Box>
    );
};

export default DeliveryReportPage;
